import { useMemo } from "react";
import {
  MdGroupAdd,
  MdRequestQuote,
  MdOutlineLocalOffer,
  MdAttachMoney,
  MdLockOpen,
} from "react-icons/md";

import useDateRange from "hooks/useDateRange";
import useAdminStats from "hooks/useAdminStats";
import PageHeader from "components/layout/PageHeader";
import KpiCard from "components/kpi/KpiCard";
import KpiStrip from "components/kpi/KpiStrip";
import ChartCard from "components/charts/ChartCard";
import SparklineChart from "components/charts/SparklineChart";
import TimeseriesChart from "components/charts/TimeseriesChart";
import FunnelChart from "components/charts/FunnelChart";
import DonutChart from "components/charts/DonutChart";
import DateRangePicker from "components/filters/DateRangePicker";
import LeaderboardCard from "components/leaderboard/LeaderboardCard";

function sumRoleDeltas(signups) {
  if (!signups) return null;
  const current =
    (signups.user?.current || 0) +
    (signups.contractor?.current || 0) +
    (signups.supplier?.current || 0);
  const previous =
    (signups.user?.previous || 0) +
    (signups.contractor?.previous || 0) +
    (signups.supplier?.previous || 0);
  const deltaPct =
    previous === 0
      ? current === 0
        ? 0
        : null
      : Math.round(((current - previous) / previous) * 1000) / 10;
  return { current, previous, deltaPct };
}

function sumKindDeltas(obj) {
  if (!obj) return null;
  const keys = Object.keys(obj);
  let current = 0;
  let previous = 0;
  for (const k of keys) {
    current += obj[k]?.current || 0;
    previous += obj[k]?.previous || 0;
  }
  const deltaPct =
    previous === 0
      ? current === 0
        ? 0
        : null
      : Math.round(((current - previous) / previous) * 1000) / 10;
  return { current, previous, deltaPct };
}

const Analytics = () => {
  const { from, to, fromIso, toIso, setPreset, set } = useDateRange(30);
  const range = useMemo(
    () => ({ from: fromIso, to: toIso }),
    [fromIso, toIso]
  );

  const overview = useAdminStats("/admin/stats/overview", range);
  const signupsContractorTs = useAdminStats("/admin/stats/timeseries", {
    metric: "signups",
    role: "contractor",
    granularity: "day",
    ...range,
  });
  const signupsSupplierTs = useAdminStats("/admin/stats/timeseries", {
    metric: "signups",
    role: "supplier",
    granularity: "day",
    ...range,
  });
  const signupsUserTs = useAdminStats("/admin/stats/timeseries", {
    metric: "signups",
    role: "user",
    granularity: "day",
    ...range,
  });
  const quotationsContractorTs = useAdminStats("/admin/stats/timeseries", {
    metric: "quotations",
    kind: "contractor",
    granularity: "day",
    ...range,
  });
  const quotationsSupplierTs = useAdminStats("/admin/stats/timeseries", {
    metric: "quotations",
    kind: "supplier",
    granularity: "day",
    ...range,
  });
  const revenueTs = useAdminStats("/admin/stats/timeseries", {
    metric: "subscriptionsRevenue",
    granularity: "day",
    ...range,
  });
  const unlocksTs = useAdminStats("/admin/stats/timeseries", {
    metric: "offerUnlocksRevenue",
    granularity: "day",
    ...range,
  });
  const funnel = useAdminStats("/admin/stats/funnel", {
    kind: "contractor",
    ...range,
  });
  const supplierFunnel = useAdminStats("/admin/stats/funnel", {
    kind: "supplier",
    ...range,
  });
  const subs = useAdminStats("/admin/stats/subscriptions", range);
  const topContractors = useAdminStats("/admin/stats/leaderboard", {
    metric: "jobs",
    role: "contractor",
    limit: 5,
    ...range,
  });
  const topSuppliers = useAdminStats("/admin/stats/leaderboard", {
    metric: "earnings",
    role: "supplier",
    limit: 5,
    ...range,
  });

  const o = overview.data?.data || {};
  const signupsAgg = sumRoleDeltas(o.signups);
  const quotationsAgg = sumKindDeltas(o.quotations);
  const chosenAgg = sumKindDeltas(o.chosen);

  const signupsSeries = useMemo(
    () => [
      {
        name: "مستخدمون",
        data: signupsUserTs.data?.buckets || [],
      },
      {
        name: "مقاولون",
        data: signupsContractorTs.data?.buckets || [],
      },
      {
        name: "موردون",
        data: signupsSupplierTs.data?.buckets || [],
      },
    ],
    [signupsUserTs.data, signupsContractorTs.data, signupsSupplierTs.data]
  );

  const quotationsSeries = useMemo(
    () => [
      {
        name: "مقاولون",
        data: quotationsContractorTs.data?.buckets || [],
      },
      {
        name: "موردون",
        data: quotationsSupplierTs.data?.buckets || [],
      },
    ],
    [quotationsContractorTs.data, quotationsSupplierTs.data]
  );

  const revenueSeries = useMemo(
    () => [
      { name: "اشتراكات", data: revenueTs.data?.buckets || [] },
      { name: "فتح عروض", data: unlocksTs.data?.buckets || [] },
    ],
    [revenueTs.data, unlocksTs.data]
  );

  const subsDonut = useMemo(() => {
    const a = subs.data?.data?.active || {};
    return [
      { label: "مستخدمون", value: a.user || 0 },
      { label: "مقاولون", value: a.contractor || 0 },
      { label: "موردون", value: a.supplier || 0 },
    ];
  }, [subs.data]);

  const funnelLabels = {
    created: "طلب جديد",
    withOffers: "وصلت عروض",
    chosen: "تم الاختيار",
    invoiced: "فاتورة",
  };

  const loadingAny =
    overview.loading ||
    signupsContractorTs.loading ||
    signupsSupplierTs.loading ||
    signupsUserTs.loading;

  return (
    <div className="mt-6">
      <PageHeader
        title="التحليلات"
        subtitle="نظرة عامة على أداء المنصة"
        filters={
          <DateRangePicker
            from={from}
            to={to}
            onPreset={setPreset}
            onChange={set}
          />
        }
      />

      <div className="mb-6">
        <KpiStrip columns={5}>
          <KpiCard
            icon={<MdGroupAdd className="h-5 w-5" />}
            title="تسجيلات جديدة"
            value={signupsAgg?.current}
            previousValue={signupsAgg?.previous}
            deltaPct={signupsAgg?.deltaPct}
            loading={overview.loading}
            sparkline={
              !signupsContractorTs.loading ? (
                <SparklineChart
                  data={signupsContractorTs.data?.buckets || []}
                  color="#2152FF"
                />
              ) : null
            }
          />
          <KpiCard
            icon={<MdRequestQuote className="h-5 w-5" />}
            title="طلبات تسعير"
            value={quotationsAgg?.current}
            previousValue={quotationsAgg?.previous}
            deltaPct={quotationsAgg?.deltaPct}
            loading={overview.loading}
            sparkline={
              !quotationsContractorTs.loading ? (
                <SparklineChart
                  data={quotationsContractorTs.data?.buckets || []}
                  color="#22c55e"
                />
              ) : null
            }
          />
          <KpiCard
            icon={<MdOutlineLocalOffer className="h-5 w-5" />}
            title="عروض جديدة"
            value={o.offers?.total?.current}
            previousValue={o.offers?.total?.previous}
            deltaPct={o.offers?.total?.deltaPct}
            loading={overview.loading}
          />
          <KpiCard
            icon={<MdAttachMoney className="h-5 w-5" />}
            title="MRR التقديري"
            format="currency"
            value={o.revenue?.mrrEstimated?.total}
            loading={overview.loading}
            suffix="ر.س"
          />
          <KpiCard
            icon={<MdLockOpen className="h-5 w-5" />}
            title="إيرادات فتح العروض"
            format="currency"
            value={o.revenue?.offerUnlocks?.current}
            previousValue={o.revenue?.offerUnlocks?.previous}
            deltaPct={o.revenue?.offerUnlocks?.deltaPct}
            loading={overview.loading}
            suffix="ر.س"
          />
        </KpiStrip>
      </div>

      <div className="mb-6 grid gap-5 xl:grid-cols-2">
        <ChartCard
          title="التسجيلات اليومية (بالدور)"
          loading={
            signupsUserTs.loading ||
            signupsContractorTs.loading ||
            signupsSupplierTs.loading
          }
          empty={
            !loadingAny &&
            signupsSeries.every((s) => s.data.length === 0 || s.data.every((d) => d.value === 0))
          }
        >
          <TimeseriesChart series={signupsSeries} type="area" />
        </ChartCard>
        <ChartCard
          title="طلبات التسعير اليومية"
          loading={
            quotationsContractorTs.loading || quotationsSupplierTs.loading
          }
        >
          <TimeseriesChart series={quotationsSeries} type="line" />
        </ChartCard>
      </div>

      <div className="mb-6 grid gap-5 xl:grid-cols-2">
        <ChartCard title="مسار التحويل: مقاول" loading={funnel.loading}>
          <FunnelChart
            stages={funnel.data?.stages || []}
            labels={funnelLabels}
          />
        </ChartCard>
        <ChartCard title="مسار التحويل: مورد" loading={supplierFunnel.loading}>
          <FunnelChart
            stages={supplierFunnel.data?.stages || []}
            labels={funnelLabels}
          />
        </ChartCard>
      </div>

      <div className="mb-6 grid gap-5 xl:grid-cols-2">
        <ChartCard
          title="الإيرادات اليومية"
          subtitle="اشتراكات + فتح عروض"
          loading={revenueTs.loading || unlocksTs.loading}
        >
          <TimeseriesChart series={revenueSeries} type="area" />
        </ChartCard>
        <ChartCard title="الاشتراكات النشطة (توزيع بالدور)" loading={subs.loading}>
          <DonutChart data={subsDonut} />
        </ChartCard>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <LeaderboardCard
          title="أفضل المقاولين (وظائف)"
          subtitle="حسب عدد الطلبات التي تم اختيارهم لها"
          items={topContractors.data?.items || []}
          loading={topContractors.loading}
          valueFormat="number"
          secondaryLabel=""
        />
        <LeaderboardCard
          title="أفضل الموردين (إيرادات)"
          subtitle="مجموع قيم الطلبات المختارة"
          items={topSuppliers.data?.items || []}
          loading={topSuppliers.loading}
          valueFormat="currency"
          secondaryFormat="number"
          secondaryLabel="طلب"
        />
      </div>

      {chosenAgg ? (
        <p className="mt-6 text-xs text-gray-400">
          * بعض الأرقام التاريخية تقريبية للسجلات السابقة لإضافة طوابع زمنية
          دقيقة.
        </p>
      ) : null}
    </div>
  );
};

export default Analytics;
